import React, { useEffect, useState } from 'react';
import { RiCloseLine, RiLock2Line, RiBook2Line } from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import ByopdfLoader from './ByopdfLoader';
import styles from './KnowledgePanel.module.css';

// The knowledge card. Locked: the book page (synopsis, cast, shape, themes,
// concepts, hook) with the payoff teased. Unlocked: everything the reader
// takes with them. `card` can be passed directly (harness/celebration);
// otherwise it's fetched by `audiobookId`.
export function KnowledgeCardView({ card, onClose }) {
  if (!card || !card.hasCard) return null;
  const unlocked = Boolean(card.unlocked);

  return (
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <button className={styles.close} onClick={onClose} aria-label="Close">
          <RiCloseLine size={20} />
        </button>

        <span className={styles.eyebrow}>
          {unlocked ? 'Taken with you' : 'What this book gives you'}
        </span>
        <h2 className={styles.title}>{card.title}</h2>
        <p className={styles.author}>{card.author}</p>

        {card.synopsis && <p className={styles.synopsis}>{card.synopsis}</p>}

        {card.hook && (
          <p className={styles.hook}>{card.hook}</p>
        )}

        {Array.isArray(card.cast) && card.cast.length > 0 && (
          <>
            <p className={styles.sectionLabel}>The cast</p>
            <ul className={styles.castList}>
              {card.cast.map((person) => (
                <li key={person.name}>
                  <strong>{person.name}</strong> · {person.line}
                </li>
              ))}
            </ul>
          </>
        )}

        {card.shape && (
          <p className={styles.shape}>
            <RiBook2Line size={14} aria-hidden="true" /> {card.shape}
          </p>
        )}

        <p className={styles.sectionLabel}>Themes</p>
        <div className={styles.chips}>
          {(card.themes || []).map((t) => <span key={t} className={styles.chip}>{t}</span>)}
        </div>

        <p className={styles.sectionLabel}>Concepts {unlocked ? 'you now own' : "you'll walk away with"}</p>
        <div className={styles.chips}>
          {(card.concepts || []).map((c) => <span key={c} className={styles.chip}>{c}</span>)}
        </div>

        {unlocked ? (
          <>
            <p className={styles.sectionLabel}>You'll finally understand</p>
            <ul className={styles.explainList}>
              {(card.understand || []).map((line) => <li key={line}>{line}</li>)}
            </ul>

            <p className={styles.sectionLabel}>You'll recognize this in</p>
            <div className={styles.chips}>
              {(card.echoes || []).map((e) => <span key={e} className={styles.chip}>{e}</span>)}
            </div>

            <p className={styles.sectionLabel}>Where to go next</p>
            <ul className={styles.castList}>
              {(card.next || []).map((item) => (
                <li key={item.title} className={item.onShelf ? styles.onShelf : undefined}>
                  <strong>{item.title}</strong> · {item.note}
                </li>
              ))}
            </ul>

            <div className={styles.question}>
              <span className={styles.eyebrow}>One great question</span>
              <p>{card.question}</p>
            </div>

            <div className={styles.footer}>
              <span className={styles.knowledgeAdded}>
                +{(card.concepts || []).length} concepts · +{(card.echoes || []).length} cultural connections · +1 great question
              </span>
            </div>
          </>
        ) : (
          <div className={styles.lockedBlock}>
            <span className={styles.lockedIcon} aria-hidden="true"><RiLock2Line /></span>
            <strong>{card.lockMessage || 'Finish this book to discover why it still shapes the way people think.'}</strong>
            <p>
              What you'll finally understand, where you'll start noticing it,
              where to go next, and this book's one great question.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function KnowledgePanel({ audiobookId, onClose }) {
  const { currentUser } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/knowledge/${encodeURIComponent(audiobookId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled && res.ok) setCard(await res.json());
      } catch (e) {
        console.error('Failed to load knowledge card:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (currentUser && audiobookId) load();
    return () => { cancelled = true; };
  }, [currentUser, audiobookId]);

  if (loading) {
    return (
      <div className={styles.backdrop} onClick={onClose}>
        <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
          <div className={styles.loading}>
            <ByopdfLoader size={48} label="Opening the card…" />
          </div>
        </div>
      </div>
    );
  }
  if (!card || !card.hasCard) {
    if (!loading) onClose?.();
    return null;
  }
  return <KnowledgeCardView card={card} onClose={onClose} />;
}

export default KnowledgePanel;
